import type { ResponsiveIframeSettings } from '../types/index.js'

/**
 * Based on https://github.com/npr/responsiveiframe/blob/master/src/jquery.responsiveiframe.js
 * @see https://npr.github.io/responsiveiframe/
 */
export class ResponsiveIframeService {
  constructor(
    private readonly iframeEl?: HTMLIFrameElement,
    private readonly settings: ResponsiveIframeSettings = {},
  ) {
    this.settings.xdomain ||= '*'
    this.settings.ie ||= navigator.userAgent.toLowerCase().includes('msie')
    this.settings.scrollToTop ||= false

    this.messageHandler = this.messageHandler.bind(this)

    // If the iframe is passed in, then we are the parent. Otherwise, we are the child.
    if (iframeEl) {
      this.init()
    } else {
      this.allowResponsiveEmbedding()
    }
  }

  private messageHandler(e: MessageEvent) {
    let height: number
    let r: RegExpMatchArray | null
    let matches = false
    let strD: string

    if (this.settings.xdomain !== '*') {
      const regex = new RegExp(this.settings.xdomain + '$')
      if (e.origin == 'null') {
        throw new Error(
          'messageHandler( elem, e): There is no origin.  You are viewing the page from your file system.  Please run through a web server.',
        )
      }
      if (e.origin.match(regex)) {
        matches = true
      } else {
        throw new Error(
          "messageHandler( elem, e): The orgin doesn't match the responsiveiframe  xdomain.",
        )
      }
    }

    if (this.settings.xdomain === '*' || matches) {
      strD = e.data + ''
      r = strD.match(/^(\d+)(s?)$/)
      if (r && r.length === 3) {
        height = parseInt(r[1], 10)
        if (!isNaN(height)) {
          try {
            this.setHeight(height)
          } catch (e) {
            console.warn(e)
          }
        }
        if (this.settings.scrollToTop && r[2] === 's') {
          scroll(0, 0)
        }
      }
    }
  }

  // Sets the height of the iframe
  private setHeight(height: number) {
    if (!this.iframeEl) {
      return
    }
    this.iframeEl.style.height = height + 'px'
  }

  // initialization for the parent, the one housing this
  private init() {
    window.addEventListener('message', this.messageHandler, false)
  }

  private messageParent() {
    let h = document.body.offsetHeight.toString()
    h = this.settings.scrollToTop ? h + 's' : h
    if (parent.postMessage) {
      parent.postMessage(h, '*')
    } else {
      window.location.hash = 'h' + h
    }
  }

  public destroy() {
    window.removeEventListener('message', this.messageHandler, false)
    window.removeEventListener('load', this.messageParent, false)
    window.removeEventListener('resize', this.messageParent, false)
  }

  public allowResponsiveEmbedding() {
    window.addEventListener('load', this.messageParent, false)
    window.addEventListener('resize', this.messageParent, false)
  }
}
